import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';

import {
  CepService,
  CustomValidators,
  ErroMessages,
  IErroMessages,
  Pattern,
  TokenService
} from 'viggo-sistemas-core-web';

import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { IMunicipio } from '@app/modules/parametrizacao/municipio/model/municipio.model';
import { MunicipioService } from '@app/modules/parametrizacao/municipio/services/municipio.service';
import { IParceiro, IParceiroEndereco } from '@app/modules/parceiro/model';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '@app/shared/components';
import { Utils } from '@app/shared/helpers/utils';
import { CepPipe } from '@app/shared/pipes';
import { locationValidationMessages } from '@app/shared/validation.message';

@Component({
  selector: 'app-fast-endereco',
  templateUrl: './fast-endereco.component.html',
  styleUrls: ['./fast-endereco.component.scss'],
  providers: [MunicipioService, CepService]
})
export class FastEnderecoComponent implements OnInit {
  @ViewChild('logradouro')
  private logradouro: ElementRef<HTMLInputElement>;

  @Input() entity: IParceiro;

  @Input() readonly = false;
  @Input() notRequired = false;

  form: UntypedFormGroup;

  cepPipe: CepPipe;
  messages = locationValidationMessages;

  constructor(
    protected fb: UntypedFormBuilder,
    protected cepService: CepService,
    protected municipioService: MunicipioService,
    protected tokenService: TokenService,
    public matSnackBar: MatSnackBar,
    public matDialog: MatDialog,
    public router: Router
  ) {
    this.cepPipe = new CepPipe();
  }

  getValue(value: string) {
    if (this.entity.enderecos && this.entity.enderecos.length > 0) {
      return this.entity.enderecos[0][value];
    }
    return null;
  }

  ngOnChanges(): void {
    if (this.tokenService.isLoggedIn()) {
      this.ngOnInit();
    }
  }

  markAllControlsAsDirty() {
    Object.keys(this.form.controls).forEach(controlKey => {
      controlKey = String(controlKey);
      const control = this.form.get(controlKey);
      if (!control || control.valid) return;
      control.markAsDirty();
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group(
      this.readonly
        ? {
            logradouro: [this.getValue('logradouro')],
            numero: [this.getValue('numero')],
            complemento: [this.getValue('complemento')],
            bairro: [this.getValue('bairro')],
            cep: [this.getValue('cep')],
            ponto_referencia: [this.getValue('ponto_referencia')],
            municipio: [this.getValue('municipio')]
          }
        : this.notRequired
        ? {
            logradouro: [
              this.getValue('logradouro'),
              [Validators.maxLength(255), CustomValidators.isEmptyValue()]
            ],
            numero: [
              this.getValue('numero'),
              [
                Validators.pattern(Pattern.patternNumberAndLetters(1, 60)),
                Validators.maxLength(60),
                CustomValidators.isEmptyValue()
              ]
            ],
            complemento: [
              this.getValue('complemento'),
              [
                Validators.maxLength(60),
                Validators.minLength(1),
                CustomValidators.isEmptyValue()
              ]
            ],
            bairro: [
              this.getValue('bairro'),
              [
                Validators.minLength(3),
                Validators.maxLength(60),
                CustomValidators.isEmptyValue()
              ]
            ],
            cep: [
              this.getValue('cep'),
              [Validators.pattern(Pattern.patternCep())]
            ],
            ponto_referencia: [
              this.getValue('ponto_referencia'),
              [Validators.maxLength(500), CustomValidators.isEmptyValue()]
            ],
            municipio: [
              this.getValue('municipio'),
              [
                CustomValidators.typeIs('object'),
                CustomValidators.isEmptyValue()
              ]
            ]
          }
        : {
            logradouro: [
              this.getValue('logradouro'),
              [
                Validators.required,
                Validators.maxLength(255),
                CustomValidators.isEmptyValue()
              ]
            ],
            numero: [
              this.getValue('numero'),
              [
                Validators.required,
                Validators.pattern(Pattern.patternNumberAndLetters(1, 60)),
                Validators.maxLength(60),
                CustomValidators.isEmptyValue()
              ]
            ],
            complemento: [
              this.getValue('complemento'),
              [
                Validators.maxLength(60),
                Validators.minLength(1),
                CustomValidators.isEmptyValue()
              ]
            ],
            bairro: [
              this.getValue('bairro'),
              [
                Validators.minLength(3),
                Validators.maxLength(60),
                Validators.required,
                CustomValidators.isEmptyValue()
              ]
            ],
            cep: [
              this.getValue('cep'),
              [Validators.required, Validators.pattern(Pattern.patternCep())]
            ],
            ponto_referencia: [
              this.getValue('ponto_referencia'),
              [Validators.maxLength(500), CustomValidators.isEmptyValue()]
            ],
            municipio: [
              this.getValue('municipio'),
              [
                CustomValidators.typeIs('object'),
                Validators.required,
                CustomValidators.isEmptyValue()
              ]
            ]
          }
    );
    this.form.controls.municipio.valueChanges.subscribe(
      (municipio?: IMunicipio) => {
        const cepForm = this.form.controls.cep;

        if (!municipio || !cepForm.value) cepForm.setValue('');
        if (typeof municipio !== 'object') cepForm.setValue('');
        if (municipio && municipio.cep)
          cepForm.setValue(this.cepPipe.transform(municipio.cep));
      }
    );
    if (this.entity.enderecos && this.entity.enderecos.length > 0) {
      this.checkCep(this.entity.enderecos[0].cep);
    }
    this.markAllControlsAsDirty();
  }

  checkCep(cep: string): void {
    const { form } = this;
    // verifica se o campo foi tocado pelo usuário e está válido
    if (form.controls.cep.valid && form.controls.cep.dirty) {
      // caso o municipio esteja setado, verifica se o cep inserido é igual o cep digitado

      form.controls.cep.setValue(this.cepPipe.transform(cep));
      // pega o cep pela api
      this.cepService.getAddres(cep).subscribe(
        addres => {
          // caso acheo local, preenche as informações dos campos
          const { controls } = form;

          if (addres.ibge && addres.localidade) {
            this.municipioService
              .getEntitiesByParams(`codigo_ibge=${addres.ibge}`)
              .subscribe(
                municipios => {
                  if (municipios && municipios.length)
                    controls.municipio.setValue(municipios[0]);
                  else controls.municipio.setValue(addres.localidade);
                  this.focarNoLogradouro();
                },
                (err: HttpErrorResponse) => this.displayError(err)
              );
          }

          if (addres.logradouro)
            controls.logradouro.setValue(addres.logradouro);
          if (addres.bairro) controls.bairro.setValue(addres.bairro);
          if (addres.complemento)
            controls.complemento.setValue(addres.complemento);
        },
        () => {
          // caso não ache um endereço adiciona o erro ao input de cep
          form.controls.cep.setErrors({ notFound: true });
          form.updateValueAndValidity();
        }
      );
    }
  }

  private focarNoLogradouro(): void {
    this.logradouro.nativeElement.focus();
    // da o click no campo de logradouro para sair do campo de autocomplete
  }

  public updateFormValue(enderecos: IParceiroEndereco[]): void {
    if (!enderecos || !enderecos.length) this.resetForm();
    else
      this.municipioService.getEntity(enderecos[0].municipio_id).subscribe(
        municipio => {
          const updatedValue: IParceiroEndereco = {
            bairro: enderecos[0].bairro || '',
            cep: enderecos[0].cep || '',
            complemento: enderecos[0].complemento || '',
            logradouro: enderecos[0].logradouro || '',
            numero: enderecos[0].numero || '',
            ponto_referencia: enderecos[0].ponto_referencia || '',
            municipio
          } as IParceiroEndereco;
          this.form.patchValue({
            ...updatedValue
          });
        },
        (err: HttpErrorResponse) => this.displayError(err)
      );
  }

  private resetForm(): void {
    this.form.reset();
  }

  getValues(): IParceiroEndereco[] {
    const { value } = this.form;

    return [
      Utils.trimAttributes(
        Utils.clearObject(
          this.notRequired
            ? {
                ...value,
                municipio: undefined,
                municipio_id: value.municipio ? value.municipio.id : undefined,
                cep: value.cep ? value.cep.replace(/\D/g, '') : undefined
              }
            : {
                ...value,
                municipio: undefined,
                cep: value.cep.replace(/\D/g, ''),
                municipio_id: value.municipio.id
              }
        )
      )
    ];
  }

  /**
   * Formatador do municipio
   */
  displayFn = (municipio: IMunicipio | string): string => {
    if (!municipio) return '';
    if (typeof municipio === 'string') return municipio;
    return `${municipio.nome} - ${municipio.sigla_uf}`;
  };

  displayError(err: HttpErrorResponse, showDefaultMsg = false) {
    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      dialogComponent: ConfirmDialogComponent,
      matDialog: this.matDialog,
      dialogData: {
        config: {
          data: {
            title: 'Aviso - Erro ao realizar operação',
            bodyMessage: err.error,
            icon: 'warning',
            icon_color: 'accent',
            singleBtn: true
          } as ConfirmDialogData,
          disableClose: true,
          width: '500px'
        } as MatDialogConfig<any>
      }
    } as IErroMessages);
    errorMsg.showMessageObject(err.error);
    // errorMsg.checkError();
  }
}
